import React, { useEffect, useMemo, useRef, useState } from "react";
import * as dmsService from "../../../services/dmsService";
import useDmsOptions from "../useDmsOptions";
import { fmtMoney } from "../../Fin/gl/glContext";
import { act, hint, linkBtn, numTh } from "../../Fin/gl/glUi";
import { AppDropdown, Field, PrimaryButton, SecondaryButton, SlidePanel, badgeStyle, cardStyle, formatDateTime, inputStyle, statusLabel, tdStyle, thStyle } from "../../../components/ui";

// Building blocks of the Dealership document screens.

const COLOURS = {
  DRAFT: ["#F3F4F6", "#374151"],
  ENTERED: ["#F3F4F6", "#374151"],
  APPRAISED: ["#F3F4F6", "#374151"],
  SCHEDULED: ["#DBEAFE", "#1E40AF"],
  REQUESTED: ["#DBEAFE", "#1E40AF"],
  PENDING_APPROVAL: ["#FEF3C7", "#92400E"],
  APPROVED: ["#DBEAFE", "#1E40AF"],
  PENDING_WITH_CUSTOMER: ["#E0E7FF", "#3730A3"],
  BOOKED: ["#DBEAFE", "#1E40AF"],
  IN_PROGRESS: ["#FEF3C7", "#92400E"],
  OUT: ["#FEF3C7", "#92400E"],
  SHIPPED: ["#FEF3C7", "#92400E"],
  PARTIALLY_DELIVERED: ["#FEF3C7", "#92400E"],
  RESERVED: ["#FEF3C7", "#92400E"],
  TRANSFER: ["#FEF3C7", "#92400E"],
  DELIVERED: ["#D1FAE5", "#065F46"],
  INVOICED: ["#D1FAE5", "#065F46"],
  WON: ["#D1FAE5", "#065F46"],
  PASSED: ["#D1FAE5", "#065F46"],
  RECEIVED: ["#D1FAE5", "#065F46"],
  RETURNED: ["#D1FAE5", "#065F46"],
  CREDITED: ["#D1FAE5", "#065F46"],
  POSTED: ["#D1FAE5", "#065F46"],
  IN_STOCK: ["#D1FAE5", "#065F46"],
  CLOSED: ["#E5E7EB", "#111827"],
  LOST: ["#FEE2E2", "#991B1B"],
  FAILED: ["#FEE2E2", "#991B1B"],
  REJECTED: ["#FEE2E2", "#991B1B"],
  EXPIRED: ["#F3F4F6", "#6B7280"],
  CANCELLED: ["#F3F4F6", "#6B7280"],
  SUPERSEDED: ["#F3F4F6", "#6B7280"],
  OPEN: ["#F3F4F6", "#374151"],
  QC: ["#E0E7FF", "#3730A3"],
  READY: ["#D1FAE5", "#065F46"],
  DONE: ["#D1FAE5", "#065F46"],
  GATE_PASSED: ["#E5E7EB", "#111827"],
  SUBMITTED: ["#DBEAFE", "#1E40AF"],
  PARTIALLY_APPROVED: ["#FEF3C7", "#92400E"],
  SETTLED: ["#E5E7EB", "#111827"],
};
const LABELS = { PENDING_WITH_CUSTOMER: "With customer", IN_STOCK: "In stock", PARTIALLY_DELIVERED: "Part delivered", OUT: "Out on drive", QC: "Quality control", GATE_PASSED: "Gate passed", PARTIALLY_APPROVED: "Part approved" };
export function DmsBadge({ status }) {
  const [bg, color] = COLOURS[status] || ["#F3F4F6", "#374151"];
  return <span style={badgeStyle(bg, color)}>{LABELS[status] || statusLabel(status)}</span>;
}

// Title, number, status and the document's actions.
export function DocHead({ title, number, status, sub, children }) {
  return (
    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
      <div>
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0" style={{ fontWeight: 700 }}>
            {title} {number}
          </h5>
          {status ? <DmsBadge status={status} /> : null}
        </div>
        {sub ? <div style={{ ...hint, marginTop: 4 }}>{sub}</div> : null}
      </div>
      <div className="d-flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function ActionHistory({ actions }) {
  if (!actions || !actions.length) return <div style={hint}>No history yet.</div>;
  return (
    <div>
      {actions
        .slice()
        .reverse()
        .map((a, i) => (
          <div key={i} style={{ borderLeft: "2px solid #E5E7EB", padding: "4px 0 10px 12px", fontSize: 13 }}>
            <div>
              <b>{statusLabel(a.action)}</b>
              {a.to_status && a.from_status !== a.to_status ? <span style={hint}> · {statusLabel(a.from_status || "")} → {statusLabel(a.to_status)}</span> : null}
            </div>
            {a.comment ? <div>{a.comment}</div> : null}
            <div style={hint}>
              {a.actor_name || "System"} · {formatDateTime(a.created_at)}
            </div>
          </div>
        ))}
    </div>
  );
}

// A checklist run: answer each question, save, or complete.
export function ChecklistForm({ checklist, onSave, disabled }) {
  const [answers, setAnswers] = useState({});
  const items = (checklist && checklist.items) || [];
  if (!checklist || !checklist.template) return <div style={hint}>No checklist is set up for this (Dealership Setup → Checklists).</div>;
  const value = (i) => (answers[i.id] !== undefined ? answers[i.id] : i.answer || "");
  const payload = () => items.map((i) => ({ item_id: i.id, answer: value(i) || null }));
  const locked = disabled || checklist.completed;
  return (
    <div>
      <div style={{ ...hint, marginBottom: 8 }}>
        {checklist.template.name}
        {checklist.completed ? ` · completed ${formatDateTime(checklist.completed_at)}` : ""}
      </div>
      {items.map((i) => (
        <div key={i.id} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14 }}>
          <div style={{ flex: 1 }}>
            {i.question}
            {i.is_mandatory ? <span style={{ color: "#C9194A" }}> *</span> : null}
          </div>
          {i.answer_type === "YESNO" ? (
            <div className="btn-group btn-group-sm" role="group" aria-label={i.question}>
              {["YES", "NO", "NA"].map((v) => (
                <button key={v} type="button" disabled={locked} className={`btn ${value(i) === v ? (v === "NO" ? "btn-danger" : "btn-success") : "btn-outline-secondary"}`} onClick={() => setAnswers({ ...answers, [i.id]: v })}>
                  {v === "NA" ? "N/A" : v === "YES" ? "Yes" : "No"}
                </button>
              ))}
            </div>
          ) : (
            <input className="form-control form-control-sm" style={{ width: 200 }} aria-label={i.question} disabled={locked} type={i.answer_type === "NUMBER" ? "number" : "text"} value={value(i)} onChange={(e) => setAnswers({ ...answers, [i.id]: e.target.value })} />
          )}
        </div>
      ))}
      {!locked ? (
        <div className="d-flex gap-2 mt-2">
          <SecondaryButton onClick={() => onSave({ answers: payload(), complete: false })}>Save</SecondaryButton>
          <PrimaryButton onClick={() => onSave({ answers: payload(), complete: true })}>Complete checklist</PrimaryButton>
        </div>
      ) : null}
    </div>
  );
}

// Totals box of a quote / order.
export function Totals({ doc, extra = [], goods = "Vehicles" }) {
  const rows = [
    [`${goods} before VAT`, Number(doc.subtotal) - Number(doc.discount_total)],
    ["of which discounts", -Number(doc.discount_total)],
    ["Services", Number(doc.charges_total)],
    ["VAT", Number(doc.tax_total)],
  ];
  return (
    <div style={{ ...cardStyle, padding: 16, maxWidth: 380, marginLeft: "auto" }}>
      {rows.map(([l, v]) => (
        <div key={l} className="d-flex justify-content-between" style={{ fontSize: 14, color: l.startsWith("of") ? "#6B7280" : undefined }}>
          <span>{l}</span>
          <span>{fmtMoney(v)}</span>
        </div>
      ))}
      <div className="d-flex justify-content-between mt-2 pt-2" style={{ borderTop: "1px solid #E5E7EB", fontWeight: 700, fontSize: 16 }}>
        <span>Total</span>
        <span>
          {doc.currency} {fmtMoney(doc.total)}
        </span>
      </div>
      {extra.map(([l, v]) => (
        <div key={l} className="d-flex justify-content-between" style={{ fontSize: 14 }}>
          <span>{l}</span>
          <span>{v}</span>
        </div>
      ))}
      {Number(doc.discount_pct) ? <div style={{ ...hint, marginTop: 6 }}>{Number(doc.discount_pct).toFixed(2)}% off list price</div> : null}
    </div>
  );
}

// The vehicle table scrolls sideways on narrow screens, which would clip a dropdown list opened inside it,
// so these lists open on the page body, above the slide-over (z-index 1055).
const overlayProps = { appendTo: typeof document !== "undefined" ? document.body : undefined, autoZIndex: false, panelStyle: { zIndex: 2000 } };
// Same height as the dropdowns beside them.
const cellInput = { height: 40, fontSize: 14 };

const blankVehicle = () => ({ variant_id: null, ext_colour_id: null, int_colour_id: null, stock_type: "NEW", unit_price: "", discount_amount: "", no_campaign: false });

// The deal editor: vehicles and services, priced live by the API (price list, campaigns, VAT).
// value: { lines, charges, header_discount }; context: { price_list_id, currency }.
export function DealEditor({ value, onChange, context, disabled }) {
  const opts = useDmsOptions(["variants", "colours:ext", "colours:int", "charge-types"], 0, { ext: { colour_type: "EXTERIOR" }, int: { colour_type: "INTERIOR" } });
  const [priced, setPriced] = useState(null);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const lines = value.lines || [];
  const charges = value.charges || [];
  const key = JSON.stringify([lines, charges, value.header_discount, context.price_list_id, context.currency]);

  useEffect(() => {
    const ready = lines.length && lines.every((l) => l.variant_id) && charges.every((c) => c.charge_type_id) && context.currency;
    if (!ready) {
      setPriced(null);
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const res = await dmsService
        .pricePreview({ price_list_id: context.price_list_id || null, currency: context.currency, header_discount: Number(value.header_discount || 0), lines: lines.map(clean), charges: charges.map(cleanCharge) })
        .catch((e) => ({ success: false, message: e.message }));
      if (mine !== seq.current) return;
      if (res?.success) {
        setPriced(res.data);
        setError(null);
      } else {
        setPriced(null);
        setError(res?.message || "Could not price the deal");
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setLine = (i, patch) => onChange({ ...value, lines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setCharge = (i, patch) => onChange({ ...value, charges: charges.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const p = priced || { lines: [], charges: [], totals: null };

  return (
    <div>
      <h6>Vehicles</h6>
      <div style={{ overflowX: "auto" }}>
        <table className="table mb-2" style={{ minWidth: 1040, tableLayout: "fixed" }}>
          <colgroup>
            <col />
            <col style={{ width: 200 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 40 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle}>Variant</th>
              <th style={thStyle}>Colour (ext / int)</th>
              <th style={thStyle}>Stock</th>
              <th style={numTh}>Price</th>
              <th style={numTh}>Discount</th>
              <th style={numTh}>Campaign</th>
              <th style={numTh}>Net</th>
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const pl = p.lines[i];
              return (
                <tr key={i}>
                  <td style={tdStyle}>
                    <AppDropdown id={`ve-${i}`} value={l.variant_id} options={opts.variants || []} onChange={(e) => setLine(i, { variant_id: e.value })} filter placeholder="Variant" disabled={disabled} {...overlayProps} />
                  </td>
                  <td style={tdStyle}>
                    <div className="d-flex flex-column gap-1">
                      <AppDropdown id={`ext-${i}`} value={l.ext_colour_id} options={opts.ext || []} onChange={(e) => setLine(i, { ext_colour_id: e.value })} placeholder="Exterior" disabled={disabled} {...overlayProps} />
                      <AppDropdown id={`int-${i}`} value={l.int_colour_id} options={opts.int || []} onChange={(e) => setLine(i, { int_colour_id: e.value })} placeholder="Interior" disabled={disabled} {...overlayProps} />
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <select className="form-select" style={cellInput} aria-label="Stock type" value={l.stock_type} disabled={disabled} onChange={(e) => setLine(i, { stock_type: e.target.value })}>
                      <option value="NEW">New</option>
                      <option value="USED">Used</option>
                      <option value="DEMO">Demo</option>
                    </select>
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} step="any" className="form-control text-end" style={cellInput} aria-label="Price" placeholder={pl ? fmtMoney(pl.list_price) : "List price"} value={l.unit_price} disabled={disabled} onChange={(e) => setLine(i, { unit_price: e.target.value })} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" min={0} step="any" className="form-control text-end" style={cellInput} aria-label="Discount" placeholder="0" value={l.discount_amount} disabled={disabled} onChange={(e) => setLine(i, { discount_amount: e.target.value })} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
                    <div style={{ minHeight: 40, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end" }}>
                      <span style={l.no_campaign ? { color: "#9CA3AF", textDecoration: "line-through" } : undefined}>{pl && pl.campaign_discount ? fmtMoney(pl.campaign_discount) : "—"}</span>
                      {!disabled && (l.no_campaign || (pl && pl.campaign_discount)) ? (
                        <button type="button" style={{ ...linkBtn, fontSize: 12, padding: 0 }} onClick={() => setLine(i, { no_campaign: !l.no_campaign })}>
                          {l.no_campaign ? "Apply campaign" : "Skip campaign"}
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums", verticalAlign: "middle" }}>{pl ? fmtMoney(pl.net_amount) : "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "center", verticalAlign: "middle" }}>
                    {!disabled && lines.length > 1 ? (
                      <button type="button" style={linkBtn} aria-label={`Remove vehicle ${i + 1}`} onClick={() => onChange({ ...value, lines: lines.filter((_, j) => j !== i) })}>
                        ✕
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!disabled ? (
        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => onChange({ ...value, lines: [...lines, blankVehicle()] })}>
          Add vehicle
        </button>
      ) : null}

      <h6>Services offered</h6>
      {charges.map((c, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ minWidth: 240, flex: 1 }}>
            <AppDropdown id={`ch-${i}`} value={c.charge_type_id} options={opts["charge-types"] || []} onChange={(e) => setCharge(i, { charge_type_id: e.value })} placeholder="Service" disabled={disabled} />
          </div>
          <input type="number" min={0} step="any" className="form-control form-control-sm text-end" style={{ width: 140 }} aria-label="Service amount" placeholder={p.charges[i] ? fmtMoney(p.charges[i].amount) : "Default"} value={c.amount ?? ""} disabled={disabled} onChange={(e) => setCharge(i, { amount: e.target.value })} />
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={!!c.is_complimentary} disabled={disabled} onChange={(e) => setCharge(i, { is_complimentary: e.target.checked })} /> Complimentary
          </label>
          {!disabled ? (
            <button type="button" style={linkBtn} onClick={() => onChange({ ...value, charges: charges.filter((_, j) => j !== i) })}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      {!disabled ? (
        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => onChange({ ...value, charges: [...charges, { charge_type_id: null, amount: "", is_complimentary: false }] })}>
          Add service
        </button>
      ) : null}

      <div className="d-flex flex-wrap align-items-start gap-3 mt-2">
        <Field label="Deal discount (spread over the vehicles)" htmlFor="hdisc" style={{ width: 260 }}>
          <input id="hdisc" type="number" min={0} step="any" className="form-control" style={inputStyle} value={value.header_discount ?? ""} disabled={disabled} onChange={(e) => onChange({ ...value, header_discount: e.target.value })} />
        </Field>
        <div style={{ flex: 1 }}>
          {error ? <div style={{ color: "#991B1B", fontSize: 13 }}>{error}</div> : null}
          {p.totals ? <Totals doc={{ ...p.totals, currency: context.currency }} /> : <div style={hint}>Choose the vehicles to see the price.</div>}
        </div>
      </div>
    </div>
  );
}

const num = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));
function clean(l) {
  return { variant_id: l.variant_id, ext_colour_id: l.ext_colour_id || null, int_colour_id: l.int_colour_id || null, stock_type: l.stock_type || "NEW", unit_id: l.unit_id || null, unit_price: num(l.unit_price), discount_amount: num(l.discount_amount) || 0, no_campaign: !!l.no_campaign };
}
function cleanCharge(c) {
  return { charge_type_id: c.charge_type_id, amount: num(c.amount), is_complimentary: !!c.is_complimentary, so_line_no: c.so_line_no || undefined };
}
export const dealPayload = (deal) => ({ lines: (deal.lines || []).map(clean), charges: (deal.charges || []).map(cleanCharge), header_discount: Number(deal.header_discount || 0) });
export const emptyDeal = () => ({ lines: [blankVehicle()], charges: [], header_discount: "" });

// A deal read back from a saved document, for editing. Ids are matched by code (they are
// re-encrypted in every response), so the options must be loaded.
export function useDealFromDoc(doc, opts) {
  return useMemo(() => {
    if (!doc) return null;
    const byCode = (list, code) => ((list || []).find((o) => o.code === code) || {}).value || null;
    return {
      lines: doc.lines
        .filter((l) => l.status !== "CANCELLED")
        .map((l) => ({ variant_id: byCode(opts.variants, l.variant_code), ext_colour_id: byCode(opts.ext, l.ext_colour_code), int_colour_id: byCode(opts.int, l.int_colour_code), stock_type: l.stock_type, unit_price: Number(l.unit_price) !== Number(l.list_price) ? Number(l.unit_price) : "", discount_amount: Number(l.discount_amount) || "", no_campaign: false })),
      charges: doc.charges.filter((c) => c.status !== "CANCELLED").map((c) => ({ charge_type_id: byCode(opts["charge-types"], c.charge_code), amount: Number(c.amount), is_complimentary: !!c.is_complimentary })),
      header_discount: Number(doc.header_discount) || "",
    };
  }, [doc, opts]);
}

// Runs a document action and reloads.
export async function run(call, after) {
  const res = await act(call);
  if (res && after) await after(res);
  return res;
}

export function Section({ title, children, right }) {
  return (
    <div style={{ ...cardStyle, padding: 16, marginBottom: 16, overflow: "visible" }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">{title}</h6>
        {right}
      </div>
      {children}
    </div>
  );
}

export function Panel({ title, onHide, children, footer, width = 980 }) {
  return (
    <SlidePanel show onHide={onHide} title={title} width={width} footer={footer}>
      {children}
    </SlidePanel>
  );
}
